import type { MouseEventHandler } from 'react';
import { KeyboardArrowDownRounded } from '@mui/icons-material';
import { Box, Chip } from '@mui/material';
import { createFocusRingStyles, designTokens } from '../../design-system';

type WorkflowFileKind = 'pdf' | 'word' | 'excel' | 'powerpoint' | 'hwp' | 'text' | 'data' | 'email' | 'image' | 'archive' | 'file';

const workflowFileVisuals: Record<WorkflowFileKind, { label: string; color: string; soft: string }> = {
  pdf: { label: 'PDF', color: designTokens.color.danger, soft: designTokens.color.dangerSoft },
  word: { label: 'W', color: designTokens.color.info, soft: designTokens.color.infoSoft },
  excel: { label: 'X', color: designTokens.color.successDark, soft: designTokens.color.successSoft },
  powerpoint: { label: 'P', color: designTokens.color.warningDark, soft: designTokens.color.warningSoft },
  hwp: { label: 'HWP', color: designTokens.color.action, soft: designTokens.color.actionSoft },
  text: { label: 'TXT', color: designTokens.color.secondaryText, soft: designTokens.color.subtleSurface },
  data: { label: '{}', color: designTokens.color.successDark, soft: designTokens.color.successSoft },
  email: { label: 'EML', color: designTokens.color.info, soft: designTokens.color.infoSoft },
  image: { label: 'IMG', color: designTokens.color.action, soft: designTokens.color.actionSoft },
  archive: { label: 'ZIP', color: designTokens.color.warningDark, soft: designTokens.color.warningSoft },
  file: { label: 'FILE', color: designTokens.color.secondaryText, soft: designTokens.color.subtleSurface },
};

function workflowFileKind(displayName: string, contentType = ''): WorkflowFileKind {
  const extension = displayName.toLowerCase().split('.').pop() ?? '';
  if (extension === 'pdf' || contentType === 'application/pdf') return 'pdf';
  if (['doc', 'docx', 'odt', 'rtf'].includes(extension) || contentType.includes('wordprocessingml') || contentType === 'application/msword') return 'word';
  if (['xls', 'xlsx', 'csv', 'ods'].includes(extension) || contentType.includes('spreadsheetml') || contentType.includes('ms-excel')) return 'excel';
  if (['ppt', 'pptx', 'odp'].includes(extension) || contentType.includes('presentationml') || contentType.includes('powerpoint')) return 'powerpoint';
  if (['hwp', 'hwpx'].includes(extension) || contentType.includes('hancom') || contentType.includes('hwp')) return 'hwp';
  if (['txt', 'md', 'log'].includes(extension) || contentType.startsWith('text/plain') || contentType.startsWith('text/markdown')) return 'text';
  if (['json', 'xml'].includes(extension) || contentType.includes('json') || contentType.includes('xml')) return 'data';
  if (extension === 'eml' || contentType === 'message/rfc822') return 'email';
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'tif', 'tiff', 'svg'].includes(extension) || contentType.startsWith('image/')) return 'image';
  if (['zip', 'rar', '7z', 'tar', 'gz'].includes(extension) || contentType.includes('zip') || contentType.includes('compressed')) return 'archive';
  return 'file';
}

/** 파일 형식은 색상뿐 아니라 세로형 문서와 확장자 약어로 구분합니다. */
export function WorkflowFileTypeIcon({ displayName, contentType, className }: { displayName: string; contentType?: string; className?: string }) {
  const kind = workflowFileKind(displayName, contentType);
  const visual = workflowFileVisuals[kind];
  const compactBadge = visual.label.length >= 3;
  const badgeWidth = visual.label.length > 3 ? 15.5 : 14.75;
  const badgeHeight = compactBadge ? 10.5 : 13;
  const badgeY = compactBadge ? 10.75 : 9.25;
  const badgeFontSize = visual.label.length > 3 ? 5.4 : compactBadge ? 6.4 : 9.5;
  const pageDetails = kind === 'excel'
    ? <g stroke={visual.color} strokeWidth="1"><path d="M15.5 12.5H24M15.5 16.5H24M15.5 20.5H24M18.3 10.8v11.4M21.2 10.8v11.4" /></g>
    : kind === 'powerpoint'
      ? <g><circle cx="20.5" cy="16.5" r="4.6" fill={visual.soft} stroke={visual.color} /><path d="M20.5 11.9v4.6h4.6" fill={visual.color} /></g>
      : kind === 'image'
        ? <g fill="none" stroke={visual.color} strokeWidth="1"><rect x="14.5" y="11" width="10.5" height="11" rx="1" /><circle cx="22.3" cy="13.8" r=".9" fill={visual.color} /><path d="m15.8 20 2.7-2.8 2 1.8 1.5-1.2 2.1 2.2" /></g>
        : kind === 'archive'
          ? <g fill={visual.color}><rect x="19" y="10.5" width="1.7" height="1.7" /><rect x="17.3" y="12.2" width="1.7" height="1.7" /><rect x="19" y="13.9" width="1.7" height="1.7" /><rect x="17.3" y="15.6" width="1.7" height="1.7" /><rect x="18.1" y="18.5" width="2.6" height="2.1" rx=".5" /></g>
          : kind === 'email'
            ? <g fill="none" stroke={visual.color} strokeWidth="1"><rect x="14.5" y="12" width="10.5" height="9.5" rx="1" /><path d="m15 13 4.8 3.7 4.7-3.7" /></g>
            : <g stroke={kind === 'word' ? visual.color : designTokens.color.faintText} strokeLinecap="round" strokeWidth="1"><path d="M15.5 12.5H24M15.5 16.5H24M15.5 20.5h7M9 25.5h14M9 29h12" /></g>;

  return <Box className={className} component="span" aria-hidden="true" data-file-kind={kind} sx={{ display: 'inline-flex', width: 20, height: 28, flex: '0 0 auto' }}>
    <svg viewBox="0 0 28 36" width="20" height="28" focusable="false">
      <path d="M6 1.5h14L26.5 8v25.25c0 .7-.55 1.25-1.25 1.25h-19c-.7 0-1.25-.55-1.25-1.25V2.75c0-.7.55-1.25 1.25-1.25Z" fill={designTokens.color.canvas} stroke={designTokens.color.mutedText} strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M20 1.5v5.25C20 7.45 20.55 8 21.25 8h5.25" fill={designTokens.color.subtleSurface} stroke={designTokens.color.mutedText} strokeWidth="1.5" strokeLinejoin="round" />
      {pageDetails}
      <rect x="0.75" y={badgeY} width={badgeWidth} height={badgeHeight} rx="2.2" fill={visual.color} />
      <text x={0.75 + badgeWidth / 2} y={badgeY + badgeHeight / 2 + badgeFontSize * 0.34} fill={designTokens.color.inverseText} textAnchor="middle" fontSize={badgeFontSize} fontWeight="750">{visual.label}</text>
    </svg>
  </Box>;
}

export type WorkflowFileChipFile = {
  displayName: string;
  contentType?: string;
  sizeBytes?: number;
  scanStatus?: string;
};

export type WorkflowFileChipProps = {
  file: WorkflowFileChipFile;
  onClick?: MouseEventHandler<HTMLElement>;
};

function formatBytes(value: number) {
  if (!Number.isFinite(value) || value < 0) return '';
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${Math.ceil(value / 1024)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}

/** 작성 화면과 요청 상세가 같은 파일 표현을 공유하고, 업무 동작은 callback으로 받습니다. */
export function WorkflowFileChip({ file, onClick }: WorkflowFileChipProps) {
  const size = typeof file.sizeBytes === 'number' ? formatBytes(file.sizeBytes) : '';
  return <Chip
    icon={<WorkflowFileTypeIcon className="WorkflowFileChip-icon" displayName={file.displayName} contentType={file.contentType} />}
    label={`${file.displayName}${file.scanStatus && file.scanStatus !== 'CLEAN' ? ` · ${file.scanStatus === 'PENDING' ? '검사 중' : file.scanStatus === 'INFECTED' ? '차단됨' : '검사 실패'}` : ''}`}
    title={`${file.displayName}${size ? ` · ${size}` : ''}`}
    aria-label={onClick ? `${file.displayName} 파일 메뉴` : undefined}
    aria-haspopup={onClick ? 'menu' : undefined}
    onClick={onClick}
    deleteIcon={onClick ? <KeyboardArrowDownRounded data-testid="workflow-file-chip-menu-indicator" /> : undefined}
    onDelete={onClick}
    variant="outlined"
    color={file.scanStatus && file.scanStatus !== 'CLEAN' ? 'warning' : 'default'}
    sx={{
      alignSelf: 'flex-start',
      height: 40,
      maxWidth: '100%',
      px: 1.5,
      gap: 1,
      borderRadius: designTokens.layout.chipRadius,
      borderColor: designTokens.color.divider,
      bgcolor: designTokens.color.navigationSurface,
      color: designTokens.color.primaryText,
      ...designTokens.typography.ui,
      fontWeight: 500,
      '&.MuiChip-clickable:hover': { borderColor: designTokens.color.strongDivider, bgcolor: designTokens.color.hoverSurface },
      '&.MuiChip-clickable:active': { bgcolor: designTokens.color.selectedNavigationSurface, boxShadow: 'none' },
      '&.Mui-focusVisible': { ...createFocusRingStyles({ inset: true }), bgcolor: designTokens.color.navigationSurface },
      '@media (hover: none)': { '&.MuiChip-clickable:hover': { borderColor: designTokens.color.divider, bgcolor: designTokens.color.navigationSurface } },
      '& .WorkflowFileChip-icon': { width: 20, height: 28, m: 0 },
      '& .MuiChip-label': { p: 0, overflow: 'hidden', textOverflow: 'ellipsis' },
      '& .MuiChip-deleteIcon': {
        width: 16,
        height: 16,
        m: 0,
        color: designTokens.color.secondaryText,
        '&:hover': { color: designTokens.color.primaryText },
      },
    }}
  />;
}
